import ReconnectingWebSocket from 'reconnecting-websocket';
import { ENV } from '@/config/env';
import { createLogger } from '@/utils/logger';

const logger = createLogger('WS_NOTIFICATIONS');

export interface RealtimeNotification {
  id: string;
  type: string;
  data: Record<string, unknown>;
  timestamp: number;
}

type NotificationCallback = (notification: RealtimeNotification) => void;

class NotificationWebSocketService {
  private ws: ReconnectingWebSocket | null = null;
  private callback: NotificationCallback | null = null;
  private currentToken: string | null = null;

  connect(token: string): void {
    // Skip if already connected with the same token
    if (this.ws && this.currentToken === token) {
      return;
    }

    // Disconnect any existing connection first
    this.disconnect();

    this.currentToken = token;
    const url = `${ENV.WS_URL}/ws/notifications?token=${encodeURIComponent(token)}`;

    logger.log('Connecting to notification WebSocket');

    this.ws = new ReconnectingWebSocket(url, [], {
      maxRetries: 10,
      connectionTimeout: 10000,
      maxReconnectionDelay: 30000,
    });

    this.ws.onopen = () => {
      logger.log('WebSocket connected');
    };

    this.ws.onclose = (event) => {
      logger.log(`WebSocket closed: code=${event.code}`);
    };

    this.ws.onerror = (event) => {
      logger.error('WebSocket error:', event);
    };

    this.ws.onmessage = (event) => {
      if (typeof event.data !== 'string') return;

      // Ignore pong responses
      if (event.data === 'pong') return;

      try {
        const notification: RealtimeNotification = JSON.parse(event.data);
        logger.log(`Received notification: ${notification.type} (${notification.id})`);

        // Notify listener
        if (this.callback) {
          this.callback(notification);
        }

        // Acknowledge receipt so the DO can remove from its queue
        this.ws?.send(JSON.stringify({
          type: 'ack',
          notificationId: notification.id,
        }));
      } catch {
        logger.error('Failed to parse WebSocket message');
      }
    };
  }

  disconnect(): void {
    if (this.ws) {
      logger.log('Disconnecting WebSocket');
      this.ws.close();
      this.ws = null;
      this.currentToken = null;
    }
  }

  onNotification(callback: NotificationCallback): void {
    this.callback = callback;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export const notificationWs = new NotificationWebSocketService();
